// Phase 43 Batch BE — canonical "asking" and "counter" resolvers for the
// frontend. Mirrors `backend/deal_basis.py`.
//
// Why this file exists
// --------------------
// Phase 42's audit (Findings B + F) found that 3 frontend surfaces
// resolved "the asking" and "the counter" with DIFFERENT fallback
// chains:
//   - IntelligencePricesTable.jsx   (asking = d.purchase_price ❌)
//   - Portfolio.jsx::recomputeOne  (counter skipped user_target_offer ❌)
//   - intelligence_summary backend (canonical ✅)
// → users saw per-row "$0 savings" while the Hero card showed $328K.
//
// This module is the single source of truth on the frontend. Any
// component reading "the asking" or "the counter" from a deal doc
// MUST use these helpers, not inline a fallback chain.
//
// Keep this byte-identical with backend/deal_basis.py — a regression
// test in test_phase43_batch_be_intelligence_drift.py pins the
// behaviour.

/**
 * Resolve the deal's "asking" price (pre-counter list price).
 *
 * Phase 48.1 — PREFERRED: read `deal.asking_price_display`, which
 * the backend now stamps on every DealPublic payload. If present, we
 * trust it verbatim. This is the "one place" fix for the recurring
 * asking-vs-counter bug class (5th recurrence closed in Phase 48.1).
 *
 * Legacy fallback chain (for older payloads or manually-constructed
 * deals in tests):
 *   original_purchase_price ▸ property.asking_price ▸ purchase_price ▸ 0
 *
 * `original_purchase_price` is the Batch AJ snapshot written by
 * `PATCH /target-offer` — the most authoritative "what the listing
 * was before I countered" value. Post-Batch AJ, `purchase_price` is
 * the EFFECTIVE price (= counter after target-offer), so using it as
 * "asking" makes the asking-vs-counter comparison degenerate to zero.
 */
export const askingFor = (d) =>
  Number(
    d?.asking_price_display ??
      d?.original_purchase_price ??
      d?.property?.asking_price ??
      d?.purchase_price ??
      0,
  );

/**
 * Resolve the deal's "counter" price (the buyer's targeted offer).
 *
 * Fallback chain:
 *   counter_offer_price ▸ user_target_offer ▸
 *   ai_negotiation.recommended_counter_offer ▸ purchase_price ▸ 0
 *
 * `user_target_offer` is the field that `PATCH /target-offer` writes
 * — pre-Phase-43, three frontend surfaces skipped it in their
 * fallback, so deals where the user countered via the Tri-View
 * "Apply target" button (the primary CTA) showed "$0 savings" on the
 * Prices table while the Hero card showed real savings.
 */
export const counterFor = (d) =>
  Number(
    d?.counter_offer_price ??
      d?.user_target_offer ??
      d?.ai_negotiation?.recommended_counter_offer ??
      d?.purchase_price ??
      0,
  );

/**
 * Convenience — returns both values plus the per-deal savings.
 * Matches `backend/deal_basis.canonical_basis`.
 */
export const basisFor = (d) => {
  const asking = askingFor(d);
  const counter = counterFor(d);
  const savings = Math.max(0, asking - counter);
  return {
    asking,
    counter,
    savingsDollar: savings,
    savingsPercent: asking > 0 ? (savings / asking) * 100 : 0,
  };
};
